import _mainCommentDataQuery from "./__generated__/mainCommentDataQuery.graphql";
import React, { Suspense } from 'react';
import { FormattedMessage } from 'react-intl-next';
import { graphql, useLazyLoadQuery } from 'react-relay/hooks';
import Button from '@atlaskit/button/new';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { Anchor, Inline, xcss } from '@atlaskit/primitives';
import { messages } from '../../../../index';
import { LinkType } from '../../../utils/analytics/types';
import { useExperienceTracker } from '../../../utils/experience-tracking/adapter/context';
import { Experiences } from '../../../utils/experience-tracking/types';
import { ReactionsWrapperComponent } from '../reactions/main';
var fullThreadLinkStyles = xcss({
  font: "var(--ds-font-body-small, normal 400 11px/16px ui-sans-serif, -apple-system, BlinkMacSystemFont, \"Segoe UI\", Ubuntu, system-ui, \"Helvetica Neue\", sans-serif)",
  fontWeight: "var(--ds-font-weight-bold, 700)",
  textDecoration: 'none'
});
var ReactReplyBarWithCommentInformation = function ReactReplyBarWithCommentInformation(_ref) {
  var _commentData$comments, _commentData$comments2, _commentData$comments3, _commentData$comments4, _commentData$comments5, _commentData$comments6, _commentData$comments7, _commentData$comments8, _commentData$comments9, _commentData$comments10;
  var experimentCohort = _ref.experimentCohort,
    cloudId = _ref.cloudId,
    commentId = _ref.commentId,
    contentLink = _ref.contentLink,
    emojiProvider = _ref.emojiProvider,
    isEditorShown = _ref.isEditorShown,
    onCommentEditorShown = _ref.onCommentEditorShown,
    onOpenFullThreadLinkClick = _ref.onOpenFullThreadLinkClick,
    showFullThreadLinkProp = _ref.showFullThreadLinkProp,
    handleMarkAsRead = _ref.handleMarkAsRead,
    experienceTrackingAttributes = _ref.experienceTrackingAttributes;
  var experienceTracker = useExperienceTracker();
  experienceTracker.start({
    name: Experiences.FETCH_COMMENT_DATA_IN_CONTEXT,
    id: 'fetchCommentData'
  });
  var commentData = useLazyLoadQuery(_mainCommentDataQuery, {
    commentId: commentId,
    cloudId: cloudId
  }, {
    fetchPolicy: 'store-and-network'
  });
  if (commentData.comments !== null) {
    experienceTracker.succeed({
      name: Experiences.FETCH_COMMENT_DATA_IN_CONTEXT,
      attributes: {
        commentId: commentId
      }
    });
  } else {
    experienceTracker.fail({
      name: Experiences.FETCH_COMMENT_DATA_IN_CONTEXT,
      error: {
        name: 'GraphQL Error',
        message: "Failed to fetch comment data for ".concat(commentId)
      }
    });
  }
  var containerId = (_commentData$comments = (_commentData$comments2 = commentData.comments) === null || _commentData$comments2 === void 0 || (_commentData$comments2 = _commentData$comments2.edges) === null || _commentData$comments2 === void 0 || (_commentData$comments2 = _commentData$comments2[0]) === null || _commentData$comments2 === void 0 || (_commentData$comments2 = _commentData$comments2.node) === null || _commentData$comments2 === void 0 || (_commentData$comments2 = _commentData$comments2.container) === null || _commentData$comments2 === void 0 ? void 0 : _commentData$comments2.id) !== null && _commentData$comments !== void 0 ? _commentData$comments : '';
  var containerType = (_commentData$comments3 = (_commentData$comments4 = commentData.comments) === null || _commentData$comments4 === void 0 || (_commentData$comments4 = _commentData$comments4.edges) === null || _commentData$comments4 === void 0 || (_commentData$comments4 = _commentData$comments4[0]) === null || _commentData$comments4 === void 0 || (_commentData$comments4 = _commentData$comments4.node) === null || _commentData$comments4 === void 0 || (_commentData$comments4 = _commentData$comments4.container) === null || _commentData$comments4 === void 0 ? void 0 : _commentData$comments4.type) !== null && _commentData$comments3 !== void 0 ? _commentData$comments3 : '';
  var isInlineComment = (_commentData$comments5 = (_commentData$comments6 = commentData.comments) === null || _commentData$comments6 === void 0 || (_commentData$comments6 = _commentData$comments6.edges) === null || _commentData$comments6 === void 0 || (_commentData$comments6 = _commentData$comments6[0]) === null || _commentData$comments6 === void 0 || (_commentData$comments6 = _commentData$comments6.node) === null || _commentData$comments6 === void 0 ? void 0 : _commentData$comments6.isInlineComment) !== null && _commentData$comments5 !== void 0 ? _commentData$comments5 : false;
  var inlineCommentIsResolved = isInlineComment && ((_commentData$comments7 = commentData.comments) === null || _commentData$comments7 === void 0 || (_commentData$comments7 = _commentData$comments7.edges) === null || _commentData$comments7 === void 0 || (_commentData$comments7 = _commentData$comments7[0]) === null || _commentData$comments7 === void 0 || (_commentData$comments7 = _commentData$comments7.node) === null || _commentData$comments7 === void 0 || (_commentData$comments7 = _commentData$comments7.location) === null || _commentData$comments7 === void 0 || (_commentData$comments7 = _commentData$comments7.inlineResolveProperties) === null || _commentData$comments7 === void 0 ? void 0 : _commentData$comments7.resolved);
  if (!containerId || containerId.length === 0 || !containerType || containerType.length === 0 || inlineCommentIsResolved) {
    return null;
  }
  FeatureGates.manuallyLogExperimentExposure('cc-notifications-react-reply-from-notif-drawer');
  if (experimentCohort === 'control') {
    return null;
  }
  var hasReplies = (_commentData$comments8 = commentData.comments) === null || _commentData$comments8 === void 0 || (_commentData$comments8 = _commentData$comments8.edges) === null || _commentData$comments8 === void 0 ? void 0 : _commentData$comments8.some(function (edge) {
    var _edge$node;
    return (edge === null || edge === void 0 || (_edge$node = edge.node) === null || _edge$node === void 0 ? void 0 : _edge$node.replies) && edge.node.replies.length > 0;
  });
  var parentCommentId = (_commentData$comments9 = (_commentData$comments10 = commentData.comments) === null || _commentData$comments10 === void 0 || (_commentData$comments10 = _commentData$comments10.edges) === null || _commentData$comments10 === void 0 || (_commentData$comments10 = _commentData$comments10.find(function (edge) {
    var _edge$node2;
    return (edge === null || edge === void 0 || (_edge$node2 = edge.node) === null || _edge$node2 === void 0 ? void 0 : _edge$node2.parentId) !== null;
  })) === null || _commentData$comments10 === void 0 || (_commentData$comments10 = _commentData$comments10.node) === null || _commentData$comments10 === void 0 ? void 0 : _commentData$comments10.parentId) !== null && _commentData$comments9 !== void 0 ? _commentData$comments9 : '';
  var hasParentCommentId = Boolean(parentCommentId);
  var showFullThreadLink = showFullThreadLinkProp || hasReplies || hasParentCommentId;
  var showEditor = function showEditor() {
    var replyCommentParentId = isInlineComment && hasParentCommentId ? parentCommentId : commentId;
    onCommentEditorShown(containerId, containerType, isInlineComment, replyCommentParentId);
  };
  var handleOpenFullThreadLinkClick = function handleOpenFullThreadLinkClick(clickType) {
    return function (event) {
      onOpenFullThreadLinkClick(LinkType.OPEN_FULL_COMMENT_THREAD, clickType, event);
    };
  };
  return /*#__PURE__*/React.createElement(Inline, {
    alignBlock: "center",
    spread: "space-between",
    testId: "notification-item-react-reply-bar"
  }, /*#__PURE__*/React.createElement(Inline, {
    space: "space.100",
    alignBlock: "center"
  }, /*#__PURE__*/React.createElement(ReactionsWrapperComponent, {
    cloudId: cloudId,
    contentId: commentId,
    containerId: containerId,
    containerType: containerType,
    isInlineComment: isInlineComment,
    emojiProvider: emojiProvider,
    handleMarkAsRead: handleMarkAsRead,
    experienceTrackingAttributes: experienceTrackingAttributes
  }), !isEditorShown && /*#__PURE__*/React.createElement(Button, {
    appearance: "subtle",
    spacing: "compact",
    onClick: showEditor
  }, /*#__PURE__*/React.createElement(FormattedMessage, messages.reply))), showFullThreadLink && /*#__PURE__*/React.createElement(Inline, {
    alignBlock: "center",
    space: "space.050"
  }, /*#__PURE__*/React.createElement(Anchor, {
    href: contentLink,
    xcss: fullThreadLinkStyles,
    testId: "open-full-thread-link",
    onClick: handleOpenFullThreadLinkClick('left'),
    onAuxClick: handleOpenFullThreadLinkClick('middle'),
    onContextMenu: handleOpenFullThreadLinkClick('right')
  }, /*#__PURE__*/React.createElement(FormattedMessage, messages.openFullThread))));
};
var ReactReplyBar = function ReactReplyBar(_ref2) {
  var experimentCohort = _ref2.experimentCohort,
    contentLink = _ref2.contentLink,
    cloudId = _ref2.cloudId,
    emojiProvider = _ref2.emojiProvider,
    commentId = _ref2.commentId,
    isEditorShown = _ref2.isEditorShown,
    onCommentEditorShown = _ref2.onCommentEditorShown,
    onOpenFullThreadLinkClick = _ref2.onOpenFullThreadLinkClick,
    showFullThreadLink = _ref2.showFullThreadLink,
    handleMarkAsRead = _ref2.handleMarkAsRead,
    experienceTrackingAttributes = _ref2.experienceTrackingAttributes;
  return /*#__PURE__*/React.createElement(Suspense, {
    fallback: null
  }, /*#__PURE__*/React.createElement(ReactReplyBarWithCommentInformation, {
    experimentCohort: experimentCohort,
    cloudId: cloudId,
    commentId: commentId,
    contentLink: contentLink,
    emojiProvider: emojiProvider,
    isEditorShown: isEditorShown,
    onCommentEditorShown: onCommentEditorShown,
    onOpenFullThreadLinkClick: onOpenFullThreadLinkClick,
    showFullThreadLinkProp: showFullThreadLink,
    handleMarkAsRead: handleMarkAsRead,
    experienceTrackingAttributes: experienceTrackingAttributes
  }));
};
export default ReactReplyBar;